import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useEffect, useState } from 'react'

import { type Transaction } from './types'
import { getLocaleDateString, sentenceCase } from '../../utils'
import TableSkeleton from '../reusable/table/Skeleton'

const columnHelper = createColumnHelper<Transaction>()

const columns = [
  columnHelper.accessor('created_at', {
    header: 'Date',
    cell: (info) => getLocaleDateString(new Date(info.getValue())),
    enableSorting: true,
    sortingFn: 'datetime',
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => `$${info.getValue().toFixed(2)}`,
  }),
  columnHelper.accessor('transaction_type', {
    header: 'Payment Mechanism',
    cell: (info) =>
      info.getValue() === 'ach' ? 'ACH' : sentenceCase(info.getValue()),
  }),
  columnHelper.accessor('description', {
    header: 'Description',
  }),
]

const defaultSortingState = [
  {
    id: 'created_at',
    desc: true,
  },
]

function TransactionsTable ({ enrollmentId }: { enrollmentId: string }) {
  const [data, setData] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting: defaultSortingState,
    },
  })

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
        const response = await fetch(
          `/api/brokers/transactions/${enrollmentId}`, // eslint-disable-line thatch/no-bare-fetch
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          }
        )
        const json = await response.json()
        if (response.ok) {
          setData(json.data?.transactions)
        } else {
          console.error(
            'Error retrieving needs attention table: ',
            json.message
          )
          setError(
            'There was an error retrieving transactions for this enrollment. Please contact support.'
          )
        }
      } catch (err: any) {
        console.error('Error retrieving needs attention table: ', err.message)
        setError(
          'There was an error retrieving transactions for this enrollment. Please contact support.'
        )
      } finally {
        setLoading(false)
      }
    }
    fetchTransactions()
  }, [enrollmentId])

  if (loading) {
    return <TableSkeleton rowCount={5} columnCount={4} />
  }

  if (error) {
    return (
      <div
        className='bg-red-100 border border-red-400 text-red-700 p-2 my-2 rounded relative text-center text-xs'
        role='alert'
      >
        {error}
      </div>
    )
  }

  if (!data.length) {
    return (
      <p className='text-base text-gray-500'>
        There are no transactions for this enrollment.
      </p>
    )
  }

  return (
    <div className='shadow ring-1 ring-black ring-opacity-5 md:rounded-lg overflow-x-auto'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead className='bg-smoke'>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className='py-3 px-2 text-left text-sm font-normal text-slate-700 sm:px-4'
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className='divide-y divide-gray-200 bg-white'>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className='whitespace-nowrap p-2 text-sm text-gray-600 sm:px-4'
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default TransactionsTable
